import React, { useEffect, useMemo, useState } from 'react';
import { api, loadBuildingsForSelector } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import {
  paymentApi,
  ReconciliationRecord,
} from '../../api/paymentApi';
import { incomeExpenseApi } from '../../api/incomeExpenseApi';
import { printVoucherHtml } from '../../utils/voucherPrint';
import { BillDetailsModal } from '../../components/modals/BillDetailsModal';
import ApartmentServiceFeePaymentModal from '../../components/modals/ApartmentServiceFeePaymentModal';
import ReconciliationSurplusModal from './ReconciliationSurplusModal';
import ReconciliationDebtModal from './ReconciliationDebtModal';
import toast from 'react-hot-toast';
import { settingsApi, PaymentGatewaySettings } from '../../services/settingsApi';

type ReconciliationGatewayFilter = PaymentGatewaySettings['gatewayType'] | '';

const RECONCILIATION_GATEWAY_OPTIONS: PaymentGatewaySettings['gatewayType'][] = [
  'SHINHAN',
  'MBB',
  'VIETQR',
  'VNPAY',
  'MOMO',
  'ZALOPAY',
  'PAYOS',
];

interface Building {
  id: string;
  name: string;
}

const GATEWAY_LABELS: Record<string, string> = {
  SHINHAN: 'Shinhan',
  MBB: 'MB Bank',
  VIETQR: 'VietQR',
  VNPAY: 'VNPay',
  MOMO: 'MoMo',
  ZALOPAY: 'ZaloPay',
  PAYOS: 'PayOS',
};

const DIRECTION_LABELS: Record<string, string> = {
  CREDIT: 'Thu',
  DEBIT: 'Chi',
  OTHER: 'Khác',
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat('vi-VN').format(Math.round(value));

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ReconciliationVoucherModal: React.FC<{
  entryId: string;
  title: string;
  onClose: () => void;
}> = ({ entryId, title, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      setHtmlContent('');
      try {
        const html = await incomeExpenseApi.fetchLedgerVoucherHtml(entryId);
        if (!cancelled) setHtmlContent(html);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [entryId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => htmlContent && printVoucherHtml(htmlContent)}
              disabled={!htmlContent}
              className="text-sm text-cyan-600 hover:text-cyan-700 disabled:text-gray-400"
            >
              In
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-xl leading-none px-2"
              aria-label="Đóng"
            >
              ×
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 relative" style={{ minHeight: '70vh' }}>
          {loading && !failed && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500" />
              <span className="ml-2 text-gray-600 text-sm">Đang tải...</span>
            </div>
          )}
          {failed ? (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm">
              Không thể tải phiếu
            </div>
          ) : htmlContent ? (
            <iframe
              title={title}
              srcDoc={htmlContent}
              className="w-full border-0 bg-white"
              style={{ minHeight: '70vh', display: 'block' }}
              sandbox="allow-same-origin allow-scripts"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export const ReconciliationTab: React.FC = () => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<ReconciliationGatewayFilter>('');
  const [enabledGateways, setEnabledGateways] = useState<PaymentGatewaySettings['gatewayType'][]>(
    [],
  );
  const [direction, setDirection] = useState('');
  const [matchStatus, setMatchStatus] = useState('');
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<ReconciliationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isShinhanSyncing, setIsShinhanSyncing] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState('');
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [surplusRow, setSurplusRow] = useState<ReconciliationRecord | null>(null);
  const [debtRow, setDebtRow] = useState<ReconciliationRecord | null>(null);
  const [paymentModal, setPaymentModal] = useState<{
    apartmentId: string;
    apartmentCode: string;
    amount: number;
    transactionDate: string;
    projectBankAccountId?: string;
    gatewayTransactionId: string;
  } | null>(null);
  const [debtContext, setDebtContext] = useState<{
    suggestedProjectBankAccountId?: string | null;
  } | null>(null);
  const [voucherPreview, setVoucherPreview] = useState<{
    entryId: string;
    title: string;
  } | null>(null);

  const canApplyActions = (row: ReconciliationRecord) =>
    row.matchStatus !== 'MATCHED' && row.direction !== 'DEBIT';

  const openFeePayment = async (
    row: ReconciliationRecord,
    apartmentId: string,
    apartmentCode: string,
  ) => {
    if (!selectedBuildingId) return;
    try {
      const ctx = await paymentApi.getReconciliationActionContext(row.id, selectedBuildingId);
      setDebtContext(ctx);
      setPaymentModal({
        apartmentId,
        apartmentCode,
        amount: ctx.transaction.amount,
        transactionDate: ctx.transaction.transactionDate.slice(0, 10),
        projectBankAccountId: ctx.suggestedProjectBankAccountId || undefined,
        gatewayTransactionId: row.id,
      });
      setDebtRow(null);
    } catch {
      toast.error('Không tải được thông tin giao dịch');
    }
  };

  useEffect(() => {
    const loadBuildings = async () => {
      try {
        const data = await loadBuildingsForSelector();
        setBuildings(data || []);
        setSelectedBuildingId((prev) =>
          applyDefaultBuildingSelection(data || [], user, prev),
        );
      } catch {
        toast.error('Không tải được danh sách chung cư');
      }
    };
    loadBuildings();

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(lastDay.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (!selectedBuildingId) {
      setEnabledGateways([]);
      return;
    }
    let cancelled = false;
    const loadGateways = async () => {
      try {
        const settings = await settingsApi.getPaymentGatewaySettings(false, selectedBuildingId);
        if (cancelled) return;
        const enabled = settings.filter((row) => row.isEnabled).map((row) => row.gatewayType);
        setEnabledGateways(enabled);
        setPaymentMethod((prev) => {
          if (prev && enabled.includes(prev)) return prev;
          if (enabled.includes('SHINHAN')) return 'SHINHAN';
          if (enabled.includes('MBB')) return 'MBB';
          return '';
        });
      } catch {
        if (!cancelled) setEnabledGateways([]);
      }
    };
    loadGateways();
    return () => {
      cancelled = true;
    };
  }, [selectedBuildingId]);

  const reconciliationGatewayOptions = useMemo(
    () =>
      RECONCILIATION_GATEWAY_OPTIONS.filter(
        (gateway) => enabledGateways.length === 0 || enabledGateways.includes(gateway),
      ),
    [enabledGateways],
  );

  const handleShinhanVaSync = async () => {
    if (!selectedBuildingId || !fromDate || !toDate) {
      toast.error('Vui lòng chọn chung cư và khoảng thời gian');
      return;
    }
    setIsShinhanSyncing(true);
    try {
      const result = await paymentApi.syncShinhanVaHistory({
        buildingId: selectedBuildingId,
        fromDate,
        toDate,
      });
      if (result.skipped) {
        toast(result.message || 'Không đồng bộ Shinhan');
      } else {
        const summary = `Shinhan: đã nhập ${result.imported ?? 0} giao dịch (${result.itemsSkipped ?? 0} bỏ qua)`;
        toast.success(
          (result.imported ?? 0) === 0 && result.message
            ? `${summary}. ${result.message}`
            : summary,
        );
      }
      await handleSearch();
    } catch (error) {
      console.error(error);
      toast.error('Đồng bộ Shinhan thất bại');
    } finally {
      setIsShinhanSyncing(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedBuildingId || !fromDate || !toDate) {
      toast.error('Vui lòng chọn chung cư và khoảng thời gian');
      return;
    }

    setIsLoading(true);
    try {
      const data = await paymentApi.getReconciliationReport({
        buildingId: selectedBuildingId,
        fromDate,
        toDate,
        paymentMethod: paymentMethod || undefined,
        direction: direction || undefined,
        matchStatus: matchStatus || undefined,
        search: search.trim() || undefined,
      });
      setRows(data || []);
    } catch (error) {
      console.error(error);
      toast.error('Không tải được dữ liệu đối soát');
    } finally {
      setIsLoading(false);
    }
  };

  const { creditTotal, debitTotal } = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        if (row.direction === 'DEBIT') {
          acc.debitTotal += row.amount || 0;
        } else {
          acc.creditTotal += row.amount || 0;
        }
        return acc;
      },
      { creditTotal: 0, debitTotal: 0 },
    );
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-8 gap-3">
          {shouldShowBuildingSelector(buildings) && (
          <select
            value={selectedBuildingId}
            onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Chọn chung cư</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>
          )}
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as ReconciliationGatewayFilter)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Tất cả cổng</option>
            {reconciliationGatewayOptions.includes('SHINHAN') && (
              <option value="SHINHAN">Shinhan</option>
            )}
            {reconciliationGatewayOptions.includes('MBB') && (
              <option value="MBB">MB Bank</option>
            )}
            {reconciliationGatewayOptions.includes('VIETQR') && (
              <option value="VIETQR">VietQR</option>
            )}
            {reconciliationGatewayOptions.includes('VNPAY') && (
              <option value="VNPAY">VNPay</option>
            )}
            {reconciliationGatewayOptions.includes('MOMO') && (
              <option value="MOMO">MoMo</option>
            )}
            {reconciliationGatewayOptions.includes('ZALOPAY') && (
              <option value="ZALOPAY">ZaloPay</option>
            )}
          </select>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Tất cả loại GD</option>
            <option value="CREDIT">Thu</option>
            <option value="DEBIT">Chi</option>
            <option value="OTHER">Khác</option>
          </select>
          <select
            value={matchStatus}
            onChange={(e) => setMatchStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="MATCHED">Đã khớp phiếu thu</option>
            <option value="UNMATCHED">Chưa khớp</option>
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm mã tham chiếu, căn hộ, mô tả..."
            className="border border-gray-300 rounded-md px-3 py-2 text-sm xl:col-span-2"
          />
        </div>
        <div className="mt-4 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">
            Ghi nhận mọi giao dịch ngân hàng từ cổng thanh toán (thu/chi/khác). Giao dịch TK mẹ chung
            chưa gán chung cư vẫn hiển thị nếu trùng số TK cấu hình.
            {enabledGateways.includes('MBB') && (
              <> MB Bank: hệ thống tự đồng bộ biến động số dư khi bấm Tìm kiếm.</>
            )}
            {enabledGateways.includes('SHINHAN') && (
              <>
                {' '}
                Shinhan: dùng &quot;Đồng bộ Shinhan&quot; khi cần kéo lịch sử VA từ ngân hàng (không
                chạy khi Tìm kiếm).
              </>
            )}{' '}
            Mã căn hộ{' '}
            <span className="text-amber-700">~A68</span> = suy luận từ nội dung CK.
          </p>
          <div className="flex shrink-0 items-center gap-2">
            {enabledGateways.includes('SHINHAN') && (
              <button
                type="button"
                onClick={handleShinhanVaSync}
                disabled={isLoading || isShinhanSyncing}
                className="px-4 py-2 border border-cyan-600 text-cyan-700 rounded-md text-sm font-medium hover:bg-cyan-50 disabled:opacity-60"
              >
                {isShinhanSyncing ? 'Đang đồng bộ...' : 'Đồng bộ Shinhan'}
              </button>
            )}
            <button
              type="button"
              onClick={handleSearch}
              disabled={isLoading || isShinhanSyncing}
              className="px-4 py-2 bg-cyan-600 text-white rounded-md text-sm font-medium hover:bg-cyan-700 disabled:opacity-60"
            >
              {isLoading ? 'Đang tải...' : 'Tìm kiếm'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Giao dịch</h2>
          <span className="text-sm text-gray-500">
            {rows.length} giao dịch · Thu {formatMoney(creditTotal)}đ · Chi {formatMoney(debitTotal)}đ
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Cổng</th>
                <th className="px-3 py-2 text-left font-medium">Loại</th>
                <th className="px-3 py-2 text-left font-medium">Mã tham chiếu</th>
                <th className="px-3 py-2 text-left font-medium">Số giao dịch</th>
                <th className="px-3 py-2 text-left font-medium">Merchant ID</th>
                <th className="px-3 py-2 text-left font-medium">Số TK</th>
                <th className="px-3 py-2 text-left font-medium">Mã căn hộ</th>
                <th className="px-3 py-2 text-left font-medium">Ngày</th>
                <th className="px-3 py-2 text-left font-medium">Mô tả</th>
                <th className="px-3 py-2 text-right font-medium">Giá trị</th>
                <th className="px-3 py-2 text-left font-medium">Khớp</th>
                <th className="px-3 py-2 text-left font-medium">Phiếu thu</th>
                <th className="px-3 py-2 text-left font-medium">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-4 py-10 text-center text-gray-500">
                    Chưa có dữ liệu đối soát trong khoảng thời gian đã chọn.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2">
                      {GATEWAY_LABELS[row.paymentGateway] || row.paymentGateway}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={
                          row.direction === 'DEBIT'
                            ? 'text-red-700'
                            : row.direction === 'CREDIT'
                              ? 'text-green-700'
                              : 'text-gray-600'
                        }
                      >
                        {DIRECTION_LABELS[row.direction] || row.direction}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.referenceId || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.bankReference || '—'}</td>
                    <td className="px-3 py-2">{row.merchantId || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.accountNumber || '—'}</td>
                    <td className="px-3 py-2">
                      {row.apartmentCode ? (
                        row.apartmentCode
                      ) : row.inferredApartmentCode ? (
                        <span className="text-amber-700" title="Suy luận từ nội dung chuyển khoản">
                          ~{row.inferredApartmentCode}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDateTime(row.transactionDate)}
                    </td>
                    <td className="px-3 py-2 min-w-[12rem] max-w-xl whitespace-normal break-words align-top">
                      {row.description || '—'}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-medium ${
                        row.direction === 'DEBIT' ? 'text-red-700' : ''
                      }`}
                    >
                      {row.direction === 'DEBIT' ? '-' : ''}
                      {formatMoney(row.amount)}đ
                    </td>
                    <td className="px-3 py-2">
                      {row.matchStatus === 'MATCHED' ? (
                        <span className="text-green-700">Đã khớp</span>
                      ) : row.isSharedMotherAccount ? (
                        <span className="text-amber-700" title="Giao dịch TK mẹ chung, chưa gán chung cư">
                          Chưa gán CH
                        </span>
                      ) : (
                        <span className="text-amber-700">Chưa khớp</span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.voucherCode ? (
                        row.billId ? (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedBillId(row.billId || '');
                              setShowBillDetails(true);
                            }}
                            className="text-cyan-700 hover:underline"
                          >
                            #{row.voucherCode}
                          </button>
                        ) : row.ledgerEntryId ? (
                          <button
                            type="button"
                            onClick={() =>
                              setVoucherPreview({
                                entryId: row.ledgerEntryId!,
                                title: 'Nhận tiền từ chuyển khoản online',
                              })
                            }
                            className="text-cyan-700 hover:underline"
                          >
                            {row.voucherCode}
                          </button>
                        ) : (
                          row.voucherCode
                        )
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {canApplyActions(row) ? (
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => setSurplusRow(row)}
                            className="text-cyan-700 hover:underline text-xs font-medium"
                          >
                            Thu dư
                          </button>
                          <button
                            type="button"
                            onClick={() => setDebtRow(row)}
                            className="text-cyan-700 hover:underline text-xs font-medium"
                          >
                            Gạch nợ
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showBillDetails && selectedBillId && (
        <BillDetailsModal
          billId={selectedBillId}
          isOpen={showBillDetails}
          onClose={() => {
            setShowBillDetails(false);
            setSelectedBillId('');
          }}
        />
      )}

      {surplusRow && selectedBuildingId && (
        <ReconciliationSurplusModal
          isOpen
          buildingId={selectedBuildingId}
          row={surplusRow}
          onClose={() => setSurplusRow(null)}
          onSuccess={handleSearch}
        />
      )}

      {debtRow && selectedBuildingId && (
        <ReconciliationDebtModal
          isOpen
          buildingId={selectedBuildingId}
          row={debtRow}
          onClose={() => setDebtRow(null)}
          onFeePayment={(apartmentId, apartmentCode) =>
            openFeePayment(debtRow, apartmentId, apartmentCode)
          }
        />
      )}

      {paymentModal && (
        <ApartmentServiceFeePaymentModal
          isOpen
          apartmentId={paymentModal.apartmentId}
          apartmentCode={paymentModal.apartmentCode}
          initialAmountReceived={paymentModal.amount}
          initialPaymentMethod="BANK_TRANSFER"
          initialPaymentDate={paymentModal.transactionDate}
          initialProjectBankAccountId={
            paymentModal.projectBankAccountId || debtContext?.suggestedProjectBankAccountId || undefined
          }
          gatewayTransactionId={paymentModal.gatewayTransactionId}
          onClose={() => {
            setPaymentModal(null);
            setDebtContext(null);
          }}
          onSuccess={() => {
            handleSearch();
            setPaymentModal(null);
            setDebtContext(null);
          }}
        />
      )}

      {voucherPreview && (
        <ReconciliationVoucherModal
          entryId={voucherPreview.entryId}
          title={voucherPreview.title}
          onClose={() => setVoucherPreview(null)}
        />
      )}
    </div>
  );
};

export default ReconciliationTab;
